import React, { useRef } from 'react';
import { Box, Fade, Popover } from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';

import { AttestationEditCard } from './AttestationEditCard';

interface AttestationEdit {
  note_section: string;
  rationale: string;
  rule: string;
  text: string;
  title: string;
}

interface AttestationEditPopoverProps {
  attestationEdit: AttestationEdit | null;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  onReject: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const AttestationEditPopover: React.FC<AttestationEditPopoverProps> = ({
  attestationEdit,
  anchorEl,
  open,
  onClose,
  onAccept,
  onReject,
  onMouseEnter,
  onMouseLeave,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  const handlePopoverEnter = () => {
    // Notify parent to cancel its close timeout
    if (onMouseEnter) {
      onMouseEnter();
    }
  };

  const handlePopoverLeave = () => {
    // Notify parent to start close timeout
    if (onMouseLeave) {
      onMouseLeave();
    }
  };

  if (!attestationEdit) {
    return null;
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transitionDuration={200}
      // Prevent scroll lock/restoration that causes scroll jumps when popover closes
      disableScrollLock
      disableRestoreFocus
      disableAutoFocus
      sx={{
        pointerEvents: 'none',
        '& .MuiPopover-paper': {
          pointerEvents: 'auto',
          maxWidth: '90vw',
          mt: 0.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'borderColors.primary',
          boxShadow: 3,
        },
      }} slots={{ transition: Fade }}
    >
      <Box
        ref={popoverRef}
        onMouseEnter={handlePopoverEnter}
        onMouseLeave={handlePopoverLeave}
        sx={{ p: 0 }}
      >
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
            >
              <AttestationEditCard
                attestationEdit={attestationEdit}
                onAccept={onAccept}
                onReject={onReject}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Popover>
  );
};

